/**
 * Which posed envelope each anatomy mode wants.
 *
 * ⚠️ GENERATED by `scripts/gen-envelope-poses.mjs` from
 * `scripts/anny/atlas-poses.json`. Do not hand-edit: every value here is a
 * measurement of an atlas's own bone geometry, and D18 forbids typing one of
 * those by hand. Regenerate with `npm run gen:envelope-poses`.
 *
 * WHAT THIS IS FOR. D16 measured that the envelope encloses the torso and not
 * the limbs, because ANNY rests in a wide A-pose while most atlases here stand
 * with their arms down — an ANGULAR difference no uniform scale can close. It
 * recorded the fix as "rigging the envelope and posing it per atlas, which is
 * real work and is not done here". This is the result of that work: one bake per
 * distinct atlas pose, chosen at runtime by the mode on screen.
 *
 * The poses, as measured:
 *
 *   bodyparts3d          8/8 segments measured — covers bodyparts3d, z-anatomy, composed, z-anatomy-regions
 *   hra                  8/8 segments measured — covers hra, composed-f
 *   hra-m                8/8 segments measured — covers hra-m
 *   htb-ct-f             8/8 segments measured — covers htb-ct-f
 *
 * ⚠️ ONLY THE TWO ADULT PRESETS ARE POSED. `child`, `elder` and `pregnant` are
 * shape studies against adult anatomy — D16a refuses that pairing as a DEFAULT
 * while allowing it as a deliberate choice — so posing them would spend assets
 * making an incoherent pairing look more convincing. They keep the rest pose.
 */

import type { BodyEnvelopeId } from './bodyEnvelopes'

/** A pose id, as measured. `null` means the envelope stays in ANNY's rest pose. */
export type EnvelopePoseId = 'bodyparts3d' | 'hra' | 'hra-m' | 'htb-ct-f'

/** The posed asset for a (pose, preset) pair. */
export const POSED_ENVELOPE_URLS: Record<
  EnvelopePoseId,
  Partial<Record<BodyEnvelopeId, string>>
> = {
  'bodyparts3d': {
    'anny-adult-f': '/models/anny-adult-f.pose-bodyparts3d.glb',
    'anny-adult-m': '/models/anny-adult-m.pose-bodyparts3d.glb',
  },
  'hra': {
    'anny-adult-f': '/models/anny-adult-f.pose-hra.glb',
    'anny-adult-m': '/models/anny-adult-m.pose-hra.glb',
  },
  'hra-m': {
    'anny-adult-f': '/models/anny-adult-f.pose-hra-m.glb',
    'anny-adult-m': '/models/anny-adult-m.pose-hra-m.glb',
  },
  'htb-ct-f': {
    'anny-adult-f': '/models/anny-adult-f.pose-htb-ct-f.glb',
    'anny-adult-m': '/models/anny-adult-m.pose-htb-ct-f.glb',
  },
}

/**
 * The pose each anatomy mode resolves to.
 *
 * Keyed by `AnatomyMode` — including the composed modes, which take the pose of
 * whichever atlas supplies their musculoskeletal system, since that is the
 * geometry that determines where the limbs are.
 */
export const MODE_POSE: Record<string, EnvelopePoseId | null> = {
  'bodyparts3d': 'bodyparts3d', // measured from bodyparts3d.ao.glb
  'composed': 'bodyparts3d', // inherits z-anatomy
  'composed-f': 'hra', // inherits hra
  'ct-atlas-f': null, // nothing measurable — rest pose
  'hra': 'hra', // measured from hra.glb
  'hra-m': 'hra-m', // measured from hra-m.glb
  'htb-ct-f': 'htb-ct-f', // measured from htb-ct-003.glb
  'z-anatomy': 'bodyparts3d', // measured from z-anatomy.ao.glb
  'z-anatomy-regions': 'bodyparts3d', // inherits z-anatomy
}

/** Every posed url, for the availability probe. */
export const POSED_ENVELOPE_URL_LIST: readonly string[] = Object.values(POSED_ENVELOPE_URLS)
  .flatMap((byPreset) => Object.values(byPreset))
  .filter((u): u is string => typeof u === 'string')

/**
 * The url to load for a mode and preset, or `null` to use the rest-pose asset.
 *
 * Returns null for any preset with no posed bake, so the caller falls back to
 * the unposed envelope rather than requesting a file that was never made.
 */
export function posedEnvelopeUrl(
  mode: string | null | undefined,
  envelope: BodyEnvelopeId,
): string | null {
  const pose = mode ? MODE_POSE[mode] : null
  if (!pose) return null
  return POSED_ENVELOPE_URLS[pose]?.[envelope] ?? null
}
